<template>
	<AppPage>
		<AppCardPage class="dossier-content">
			<AppCardPageTop>
				<h2 class="text-2xl">Dossiers</h2>
				<div class="right">
					<AppLabel :label="loadingStore.dossiersMessage" />
					<input
						id="current-search"
						ref="searchInput"
						v-model="currentSearch"
						type="text"
						placeholder="Zoek een dossier"
						@keydown.enter.prevent="submitSearch"
					/>
					<AppButton
						as="RouterLink"
						variant="primary"
						class="self-stretch my-0.5"
						:to="{ name: 'Nieuw Dossier Maken' }"
					>
						+ Nieuw dossier maken
					</AppButton>
				</div>
			</AppCardPageTop>
			<AppCard v-if="availableDossiers" no-padding>
				<div>
					<div
						class="flex justify-between text-sm font-semibold p-2 border-b pr-8"
					>
						<div
							class="sortable"
							:class="{ [sortOrder]: sortedColumn === 'name' }"
							tabindex="0"
							@click="sort('name')"
							@keydown.enter="sort('name')"
							@keydown.space.prevent="sort('name')"
						>
							<span class="flex icon-container">Dossiernaam</span>
						</div>
						<div
							class="sortable"
							:class="{ [sortOrder]: sortedColumn === 'lastOpened' }"
							tabindex="0"
							@click="sort('lastOpened')"
							@keydown.enter="sort('lastOpened')"
							@keydown.space.prevent="sort('lastOpened')"
						>
							<span class="flex icon-container">Dossier geopend</span>
						</div>
					</div>
					<RecycleScroller
						v-slot="{ item, index }"
						page-mode
						:items="matches"
						:item-size="ITEM_SIZE"
						key-field="id"
						:buffer="500"
						:prerender="Math.ceil(height / ITEM_SIZE)"
					>
						<DossierListRow
							:dossier="item"
							:class="{
								loading: loadingStore.selectedDossier?.id === item.id,
							}"
							:is-last="index === matches.length - 1"
						/>
					</RecycleScroller>
					<div
						v-if="matches?.length === 0"
						:style="`height: ${ITEM_SIZE}px`"
						class="disabled nothing-found flex justify-center items-center"
					>
						<span>Geen dossiers gevonden</span>
					</div>
				</div>
			</AppCard>
			<div class="dossier-content">
				<span v-if="loadingStore.setSelectedDossierFailed" class="error-banner">
					{{ loadingStore.setSelectedDossierMessage }}
				</span>
			</div>
		</AppCardPage>
	</AppPage>
</template>

<script lang="ts" setup>
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { useWindowSize } from "@vueuse/core";
import { useShortcut } from "@/composables/useShortcut";
import { sortMatches, searchAllDossiers } from "@/helpers/dossierHelpers";
import { useLoadingStore } from "@/store/useLoadingStore";
import { SortOrder } from "@/enums/SortOrder";
import type { Row } from "@rocicorp/zero";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";

const ITEM_SIZE = 45;

const { data: availableDossiers } = useQuery(() => queries.dossier.all());

const { height } = useWindowSize();

const currentSearch = ref("");
const searchInput = ref<HTMLInputElement | null>(null);

const sortedColumn = ref<keyof Row["dossier"]>("lastOpened");
const sortOrder: Ref<SortOrder> = ref(SortOrder.Descending);

const loadingStore = useLoadingStore();

function sort(columnModel: keyof Row["dossier"]) {
	if (sortedColumn.value === columnModel) {
		switch (sortOrder.value) {
			case SortOrder.Ascending:
				sortOrder.value = SortOrder.Descending;
				break;
			default:
				sortOrder.value = SortOrder.Ascending;
		}
	} else {
		sortedColumn.value = columnModel;

		switch (columnModel) {
			case "creationDate":
			case "lastOpened":
				sortOrder.value = SortOrder.Descending;
				break;
			default:
				sortOrder.value = SortOrder.Ascending;
		}
	}
}

const matches = computed(() => {
	let matches = availableDossiers.value;
	if (currentSearch.value.trim()?.length >= 1) {
		matches = searchAllDossiers(currentSearch.value, availableDossiers.value);
	}

	return sortMatches(matches, sortedColumn.value, sortOrder.value);
});

function submitSearch(e: Event) {
	if (e.target instanceof HTMLInputElement) {
		e.target.blur();
	}

	const firstDossierRow = document.querySelector(`.dossier-row a`);
	if (firstDossierRow instanceof HTMLElement) {
		firstDossierRow.focus();
	}
}

useShortcut(["Control", "f"], () => {
	searchInput.value?.focus();
	searchInput.value?.select();
});
useShortcut("/", () => searchInput.value?.focus());
</script>

<style lang="scss">
.dossier-content {
	.error-banner {
		width: 100%;
		display: block;
		background: var(--color-fail);
		color: var(--text-invert);
		padding: 8px;
		border-radius: var(--border-radius);
	}

	#current-search {
		padding: 0.375rem 0.625rem;
		width: 250px;
		border-radius: var(--border-radius);
		border: 1px solid var(--border);
		background: white;
	}

	.table-wrapper {
		position: relative;

		.dossier-loader {
			position: absolute;
			flex: 1;
			top: 35px;
			left: 50%;
			transform: translateX(-50%);

			span {
				text-shadow: 0px 0px 10px #fff;
			}
		}
	}

	.app-card-table {
		th,
		td {
			border-left: none;
		}

		.table-cell-wrap {
			.column-name {
				display: flex;
				align-items: center;
				gap: 0.25rem;
			}
		}
	}

	.nothing-found {
		text-align: center;
		color: var(--text-muted);
		user-select: text;
	}

	#lastOpened,
	#creationDate {
		font-size: 0.85rem;
		color: var(--text-muted);
	}
}

.app-card-page-top {
	.right {
		display: flex;
		align-items: center;
		column-gap: 10px;
	}
}
</style>
